import qs from 'query-string'

/**
 * Check if IntersectionObserver is natively enabled.
 * @return {Boolean} True if IntersectionObserver is enabled, false otherwise.
 */
export const isIntersectionObserverEnabled = () => 'IntersectionObserver' in window

/**
 * Set up an IntersectionObserver.
 *
 * Accepts a selector or an array of selectors, and executes the callback
 * function for each selector when it enters the viewport.
 *
 * For more info, see: <https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API>
 *
 * @param {DOMString|Array} selectors HTML selector for the observer.
 * @param {Function} callback Function to run when an element enters the viewpoint.
 * @param {Array} threshold Threshold to trigger the IntersectionObserver.
 */
export const setupIntersectionObserver = (selectors, callback, threshold = [0]) => {
    const io = new IntersectionObserver(
        (entries) => {
            ;[...entries].forEach(({ isIntersecting, _, target }) => {
                if (isIntersecting) {
                    callback(target)
                    io.unobserve(target)
                }
            })
        },
        { threshold },
    )

    if (!selectors || !callback) {
        return
    }

    for (const selector of [].concat(selectors)) {
        for (const element of [...document.querySelectorAll(selector)]) {
            io.observe(element)
        }
    }
}

/**
 * Set up an AJAX call using Fetch.
 * @param {string} url
 * @param {Object} params Params to send to the Ajax call.
 * @param {string} method
 * @return {Promise}
 */
export const ajax = async (url, params, method = 'POST', asJson = false, addWcNonce = false) => {
    const body = qs.stringify(
        {
            ...params,
            lang: window.customData.lang,
        },
        { arrayFormat: 'index' },
    )

    let header = {
        'Content-Type': `${asJson ? 'application/json' : 'application/x-www-form-urlencoded'}; charset=utf-8`,
        'X-WP-Nonce': window.customData.apiNonce,
    }

    if (addWcNonce) {
        header = {
            ...header,
            Nonce: window.customData.apiWcNonce,
        }
    }

    const args = {
        method,
        credentials: 'same-origin',
        headers: new Headers(header),
    }
    if (method === 'GET') {
        url += '?' + body
    } else {
        args.body = asJson ? JSON.stringify(params) : body
    }
    return fetch(url, args)
}

export const classList = function (element) {
    const list = element.classList

    return {
        toggle: function (className) {
            list.toggle(className)
            return this
        },
        add: function (className) {
            list.add(className)
            return this
        },
        remove: function (className) {
            list.remove(className)
            return this
        },
    }
}

export const getSiblings = function (e) {
    const siblings = []

    if (!e.parentNode) {
        return siblings
    }

    let sibling = e.parentNode.firstChild

    while (sibling) {
        if (sibling.nodeType === 1 && sibling !== e) {
            siblings.push(sibling)
        }
        sibling = sibling.nextSibling
    }
    return siblings
}

export const elementIsFirst = function (element) {
    const parent = element.parentNode
    return element === parent.firstElementChild
}

export const elementIsLast = function (element) {
    const parent = element.parentNode
    return element === parent.lastElementChild
}

export const getHeaderElements = function () {
    return [
        document.querySelector('#wpadminbar'),
        document.querySelector('#countdown'),
        ...Array.from(document.querySelectorAll('#site-header > *')).filter((element) => element.tagName !== 'NAV'),
    ].filter((element) => element)
}

export const calculateHeaderHeight = function () {
    const headerAndCountdown = getHeaderElements().reduce((acc, element) => acc + element?.offsetHeight || 0, 0)
    const countdownElement = document.querySelector('#countdown')
    const countdownOffset = countdownElement ? countdownElement.getBoundingClientRect().top : 0
    const heightWithOffset = headerAndCountdown + countdownOffset
    const headerHeight = document.querySelector('#site-header')?.offsetHeight || 0

    return heightWithOffset < headerHeight ? headerHeight : heightWithOffset
}
